import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useProductModulesList,
  useProductModuleMutations,
} from "../hooks/useProductModules";
import type { ProductModule, ProductSubmodule } from "@/modules/issues/types";

function errMessage(err: unknown): string {
  return (err as Error)?.message ?? "Something went wrong";
}

export function ProductModulesTab({ productId }: { productId: string }) {
  const { data: modules = [], isLoading } = useProductModulesList(productId);
  const m = useProductModuleMutations(productId);

  const [newModuleName, setNewModuleName] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<
    | { kind: "module" | "submodule"; id: string; name: string }
    | null
  >(null);

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function addModule() {
    const name = newModuleName.trim();
    if (!name) return;
    setError(null);
    try {
      await m.createModule.mutateAsync(name);
      setNewModuleName("");
    } catch (err) {
      setError(errMessage(err));
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setError(null);
    try {
      if (deleteTarget.kind === "module") {
        await m.deleteModule.mutateAsync(deleteTarget.id);
      } else {
        await m.deleteSubmodule.mutateAsync(deleteTarget.id);
      }
      setDeleteTarget(null);
    } catch (err) {
      setError(errMessage(err));
      setDeleteTarget(null);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Module Taxonomy</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Define the Module → Submodule/Page structure used by QA issues for this
          product. Deactivate an item to hide it from new issues while preserving
          history; deletion is blocked while issues still reference it.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      {/* Add module */}
      <div className="flex items-center gap-2">
        <Input
          value={newModuleName}
          onChange={(e) => setNewModuleName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addModule();
            }
          }}
          placeholder="New module name..."
          className="max-w-xs"
        />
        <Button onClick={addModule} disabled={!newModuleName.trim() || m.createModule.isPending}>
          {m.createModule.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
          ) : (
            <Plus className="h-4 w-4" strokeWidth={1.5} />
          )}
          Add Module
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading modules...
        </div>
      ) : modules.length === 0 ? (
        <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
          No modules yet. Add one above.
        </div>
      ) : (
        <div className="space-y-2">
          {modules.map((mod) => (
            <ModuleCard
              key={mod.id}
              module={mod}
              expanded={expanded.has(mod.id)}
              onToggleExpand={() => toggleExpand(mod.id)}
              mutations={m}
              onError={setError}
              onRequestDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.kind ?? "item"}?`}
        description={
          deleteTarget
            ? `Delete '${deleteTarget.name}'? This cannot be undone. If issues reference it, deletion will be blocked — deactivate it instead.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function ModuleCard({
  module: mod,
  expanded,
  onToggleExpand,
  mutations: m,
  onError,
  onRequestDelete,
}: {
  module: ProductModule;
  expanded: boolean;
  onToggleExpand: () => void;
  mutations: ReturnType<typeof useProductModuleMutations>;
  onError: (msg: string) => void;
  onRequestDelete: (t: { kind: "module" | "submodule"; id: string; name: string }) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(mod.name);
  const [newSubName, setNewSubName] = useState("");

  async function saveName() {
    const name = nameDraft.trim();
    if (!name || name === mod.name) {
      setEditing(false);
      return;
    }
    try {
      await m.updateModule.mutateAsync({ id: mod.id, data: { name } });
      setEditing(false);
    } catch (err) {
      onError(errMessage(err));
    }
  }

  async function toggleActive() {
    try {
      await m.updateModule.mutateAsync({ id: mod.id, data: { isActive: !mod.isActive } });
    } catch (err) {
      onError(errMessage(err));
    }
  }

  async function addSub() {
    const name = newSubName.trim();
    if (!name) return;
    try {
      await m.createSubmodule.mutateAsync({ moduleId: mod.id, name });
      setNewSubName("");
    } catch (err) {
      onError(errMessage(err));
    }
  }

  return (
    <div className="rounded-lg border bg-card" style={{ borderColor: "hsl(210,30%,88%)" }}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
          ) : (
            <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
          )}
        </button>

        {editing ? (
          <div className="flex flex-1 items-center gap-2">
            <Input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveName()}
              className="h-8 max-w-xs"
              autoFocus
            />
            <Button size="sm" onClick={saveName}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setNameDraft(mod.name); }}>
              Cancel
            </Button>
          </div>
        ) : (
          <>
            <span className="flex-1 text-sm font-medium text-foreground">
              {mod.name}
              <span className="ml-2 text-xs text-muted-foreground">
                ({mod.submodules.length})
              </span>
            </span>
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={
                mod.isActive
                  ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
                  : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
              }
            >
              {mod.isActive ? "Active" : "Inactive"}
            </span>
            <button
              type="button"
              onClick={() => { setNameDraft(mod.name); setEditing(true); }}
              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
              title="Rename"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={toggleActive}
              className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
              title={mod.isActive ? "Deactivate" : "Reactivate"}
            >
              {mod.isActive ? "Deactivate" : "Reactivate"}
            </button>
            <button
              type="button"
              onClick={() => onRequestDelete({ kind: "module", id: mod.id, name: mod.name })}
              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-red-50 hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </>
        )}
      </div>

      {expanded && (
        <div className="border-t px-3 py-2" style={{ borderColor: "hsl(210,30%,92%)" }}>
          {mod.submodules.length === 0 ? (
            <p className="py-1 text-xs text-muted-foreground">No submodules.</p>
          ) : (
            <div className="space-y-1">
              {mod.submodules.map((sub) => (
                <SubmoduleRow
                  key={sub.id}
                  submodule={sub}
                  mutations={m}
                  onError={onError}
                  onRequestDelete={onRequestDelete}
                />
              ))}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <Input
              value={newSubName}
              onChange={(e) => setNewSubName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSub()}
              placeholder="New submodule / page..."
              className="h-8 max-w-xs"
            />
            <Button size="sm" variant="outline" onClick={addSub} disabled={!newSubName.trim()}>
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmoduleRow({
  submodule: sub,
  mutations: m,
  onError,
  onRequestDelete,
}: {
  submodule: ProductSubmodule;
  mutations: ReturnType<typeof useProductModuleMutations>;
  onError: (msg: string) => void;
  onRequestDelete: (t: { kind: "module" | "submodule"; id: string; name: string }) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(sub.name);

  async function saveName() {
    const name = nameDraft.trim();
    if (!name || name === sub.name) {
      setEditing(false);
      return;
    }
    try {
      await m.updateSubmodule.mutateAsync({ id: sub.id, data: { name } });
      setEditing(false);
    } catch (err) {
      onError(errMessage(err));
    }
  }

  async function toggleActive() {
    try {
      await m.updateSubmodule.mutateAsync({ id: sub.id, data: { isActive: !sub.isActive } });
    } catch (err) {
      onError(errMessage(err));
    }
  }

  return (
    <div className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-[hsl(210,50%,98%)]">
      {editing ? (
        <>
          <Input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveName()}
            className="h-7 max-w-xs"
            autoFocus
          />
          <Button size="sm" onClick={saveName}>Save</Button>
          <button
            type="button"
            onClick={() => { setEditing(false); setNameDraft(sub.name); }}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm text-foreground">{sub.name}</span>
          {!sub.isActive && (
            <span className="text-[10px] text-muted-foreground">Inactive</span>
          )}
          <button
            type="button"
            onClick={() => { setNameDraft(sub.name); setEditing(true); }}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            title="Rename"
          >
            <Pencil className="h-3 w-3" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={toggleActive}
            className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            {sub.isActive ? "Deactivate" : "Reactivate"}
          </button>
          <button
            type="button"
            onClick={() => onRequestDelete({ kind: "submodule", id: sub.id, name: sub.name })}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-red-50 hover:text-red-600"
            title="Delete"
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
          </button>
        </>
      )}
    </div>
  );
}
